import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CalendarPlusIcon, CheckIcon, CreditCardIcon, IdCardIcon, PlusIcon, ShieldCheckIcon, XIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { participants, type Activity } from '../../data/loisirs';
interface RegistrationFlowProps {
  activity: Activity | null;
  onClose: () => void;
}
const steps = ['Participant', 'Activity', 'Identity', 'Payment', 'Confirmation'];
export function RegistrationFlow({
  activity,
  onClose
}: RegistrationFlowProps) {
  const [step, setStep] = useState(0);
  const [participant, setParticipant] = useState(participants[1].id);
  const close = () => {
    onClose();
    setStep(0);
  };
  const selected = participants.find(person => person.id === participant);
  return <AnimatePresence data-id="element-1559" data-mp="1wd08es7w7vtg">
      {activity && <motion.div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-1560" data-mp="1we1juxstmkbp">
          <button type="button" aria-label="Close" onClick={close} className="absolute inset-0 bg-ville-navy/40" data-id="element-1561" data-mp="36x7a72ctl23" />

          <motion.div role="dialog" aria-modal="true" aria-label={`Registration for ${activity.name}`} initial={{
        y: 24,
        opacity: 0,
        scale: 0.98
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 16,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl sm:p-8" data-id="element-1562" data-mp="3uerj5ea944nt">
            <div className="flex items-start justify-between gap-4" data-id="element-1563" data-mp="c00j6axcppum">
              <div data-id="element-1564" data-mp="31qnbin84dhih">
                <p className="text-xs font-bold uppercase tracking-wider text-ville-indigo" data-id="element-1565" data-mp="31r5hibkl3a3l">
                  Step {Math.min(step + 1, steps.length)} of {steps.length}
                </p>
                <h2 className="mt-1 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-1566" data-mp="1osddvrmj98xt">{activity.name}</h2>
              </div>
              <button type="button" onClick={close} aria-label="Close" className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1567" data-mp="1sqjf6k4lqwbc">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-1568" data-mp="1518fl5yzn7ys" />
              </button>
            </div>

            <div className="mt-6" data-id="element-1569" data-mp="w2d87qk7jrtw">
              {step === 0 && <div data-id="element-1570" data-mp="w3o7pe6medf7">
                  <h3 className="text-base font-bold text-ville-navy" data-id="element-1571" data-mp="w0a6i99p0a9c">Who is this for?</h3>
                  <ul className="mt-4 flex flex-wrap gap-3" data-id="element-1572" data-mp="111pt45riqotu">
                    {participants.map(person => {
                const active = person.id === participant;
                return <li key={person.id} data-id="element-1573" data-mp="r28ozsrx0izz">
                          <button type="button" aria-pressed={active} onClick={() => setParticipant(person.id)} className={`flex w-24 flex-col items-center gap-2 rounded-2xl border px-3 py-4 transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'border-ville-indigo bg-ville-indigo-soft' : 'border-slate-200 hover:border-ville-indigo'}`} data-id="element-1574" data-mp="r54npjbl7bph">
                            <span className={`grid h-12 w-12 place-items-center rounded-full text-sm font-bold ${active ? 'bg-ville-indigo text-white' : 'bg-slate-100 text-ville-navy'}`} aria-hidden="true" data-id="element-1575" data-mp="qgclyh7izazc">
                              {person.initials}
                            </span>
                            <span className="text-sm font-bold text-ville-navy" data-id="element-1576" data-mp="10808c3p861ty">{person.name}</span>
                            <span className="text-xs text-slate-500" data-id="element-1577" data-mp="v9f9o8pmfq7m">{person.detail}</span>
                          </button>
                        </li>;
              })}
                    <li data-id="element-1578" data-mp="156toy4zt68yc">
                      <button type="button" className="flex w-24 flex-col items-center gap-2 rounded-2xl border border-dashed border-slate-300 px-3 py-4 text-slate-500 transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1579" data-mp="153fjgnvuljmo">
                        <span className="grid h-12 w-12 place-items-center rounded-full bg-slate-50" aria-hidden="true" data-id="element-1580" data-mp="3ddf3wmc578hg">
                          <PlusIcon className="h-5 w-5" data-id="element-1581" data-mp="2fqeyes3k9kuv" />
                        </span>
                        <span className="text-sm font-bold" data-id="element-1582" data-mp="2kp9f9ohywzcc">Add</span>
                        <span className="text-xs" data-id="element-1583" data-mp="2pnkqa2k01fw5">a member</span>
                      </button>
                    </li>
                  </ul>
                </div>}

              {step === 1 && <dl className="space-y-3 rounded-2xl border border-slate-200 p-5 text-sm" data-id="element-1584" data-mp="2uk1cusbjvm74">
                  <div className="flex justify-between gap-4" data-id="element-1585" data-mp="4z6gdsoh97w5">
                    <dt className="text-slate-500" data-id="element-1586" data-mp="9v3stnwmdu3n">Activity</dt>
                    <dd className="font-bold text-ville-navy" data-id="element-1587" data-mp="1udxylaipmwpp">{activity.name}</dd>
                  </div>
                  <div className="flex justify-between gap-4" data-id="element-1588" data-mp="1pfcsiugs1itc">
                    <dt className="text-slate-500" data-id="element-1589" data-mp="1ph7gyir9bt26">Schedule</dt>
                    <dd className="font-bold text-ville-navy" data-id="element-1590" data-mp="1kncs70wsonw8">{activity.schedule}</dd>
                  </div>
                  <div className="flex justify-between gap-4" data-id="element-1591" data-mp="1foi5hnvpif6u">
                    <dt className="text-slate-500" data-id="element-1592" data-mp="1aqq7cc4gxwu7">Location</dt>
                    <dd className="font-bold text-ville-navy" data-id="element-1593" data-mp="1ao41qi151rem">{activity.place}</dd>
                  </div>
                  <div className="flex justify-between gap-4" data-id="element-1594" data-mp="16qzjag6btutd">
                    <dt className="text-slate-500" data-id="element-1595" data-mp="11q23294vnaui">Participant</dt>
                    <dd className="font-bold text-ville-navy" data-id="element-1596" data-mp="wqz0cvcddgpp">{selected?.name}</dd>
                  </div>
                  <div className="flex justify-between gap-4 border-t border-slate-100 pt-3" data-id="element-1597" data-mp="2w3tad1zqhims">
                    <dt className="text-slate-500" data-id="element-1598" data-mp="2r46vko49bubv">Price</dt>
                    <dd className="text-base font-extrabold text-ville-navy" data-id="element-1599" data-mp="2r2ngv1y91rc7">{activity.price}</dd>
                  </div>
                </dl>}

              {step === 2 && <div className="space-y-3" data-id="element-1600" data-mp="u6su2h0a9e5z">
                  {[{
              icon: IdCardIcon,
              label: 'Digital card verified',
              detail: 'Citizen card of Bryon Mickelson'
            }, {
              icon: ShieldCheckIcon,
              label: 'Residency confirmed',
              detail: '5108 - 46 Street, Bonnyville'
            }, {
              icon: CheckIcon,
              label: 'Eligibility validated',
              detail: `${selected?.name} · ${activity.audience}`
            }].map(item => <div key={item.label} className="flex items-start gap-3 rounded-2xl bg-slate-50 px-4 py-3" data-id="element-1601" data-mp="y7cqtjgy2frl">
                      <item.icon className="mt-0.5 h-5 w-5 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-1602" data-mp="138sb39basi8x" />
                      <div data-id="element-1603" data-mp="3j06dr4y8hhg8">
                        <p className="text-sm font-bold text-ville-navy" data-id="element-1604" data-mp="3nxg2eidstp30">{item.label}</p>
                        <p className="text-sm text-slate-500" data-id="element-1605" data-mp="25z1mbr7qubph">{item.detail}</p>
                      </div>
                    </div>)}
                  <p className="text-sm text-slate-500" data-id="element-1606" data-mp="20z5ibrz1qk9x">
                    Nothing to type again: it all comes from your citizen profile.
                  </p>
                </div>}

              {step === 3 && <div data-id="element-1607" data-mp="2azfv5bh65pok">
                  <div className="flex items-center gap-4 rounded-2xl border border-ville-indigo bg-ville-indigo-soft px-5 py-4" data-id="element-1608" data-mp="2az62th8nj6fp">
                    <CreditCardIcon className="h-5 w-5 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-1609" data-mp="1i9peovs2ebpa" />
                    <div className="min-w-0 flex-1" data-id="element-1610" data-mp="1w13xp2lz2cwc">
                      <p className="text-sm font-bold text-ville-navy" data-id="element-1611" data-mp="1w2ot0ug41zv9">Visa •••• 4127</p>
                      <p className="text-sm text-slate-600" data-id="element-1612" data-mp="1r9lwqejy8cbd">Saved payment method</p>
                    </div>
                    <span className="text-base font-extrabold text-ville-navy" data-id="element-1613" data-mp="13j9qr989kpnf">{activity.price}</span>
                  </div>
                  <p className="mt-3 text-sm text-slate-500" data-id="element-1614" data-mp="zh4x4ahtft0s">
                    Your receipt will be filed under My Profile › My Invoices.
                  </p>
                </div>}

              {step === 4 && <div className="text-center" data-id="element-1615" data-mp="1tf7lalciwg7v">
                  <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" aria-hidden="true" data-id="element-1616" data-mp="1xef7ea1eu4gb">
                    <CheckIcon className="h-7 w-7" data-id="element-1617" data-mp="22boydu4g6o69" />
                  </span>
                  <h3 className="mt-4 text-xl font-extrabold text-ville-navy" data-id="element-1618" data-mp="27ccugu7j9v7c">Registration confirmed</h3>
                  <p className="mt-2 text-sm text-slate-600" data-id="element-1619" data-mp="2ca4obtrqnre6">
                    The activity has been added to your digital card. Your receipt is available under My Profile.
                  </p>
                  <div className="mt-6 flex flex-wrap justify-center gap-3" data-id="element-1620" data-mp="2l3hp1hw1nxat">
                    <Link to="/my-card" className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-5 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5" data-id="element-1621" data-mp="2q4owtgy6swhh">
                      <IdCardIcon className="h-4 w-4" aria-hidden="true" data-id="element-1622" data-mp="357oy89mclnd4" />
                      View my card
                    </Link>
                    <button type="button" className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo" data-id="element-1623" data-mp="2v87rdovd13fk">
                      <CalendarPlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1624" data-mp="3nw3o2tkxivcc" />
                      Add to my calendar
                    </button>
                  </div>
                </div>}
            </div>

            {step < 4 && <div className="mt-8 flex items-center justify-between gap-4" data-id="element-1625" data-mp="3e4pjw8i197cv">
                <button type="button" onClick={() => step === 0 ? close() : setStep(step - 1)} className="text-sm font-semibold text-slate-500 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1626" data-mp="3a58eff2lwa7p">
                  {step === 0 ? 'Cancel' : 'Back'}
                </button>
                <button type="button" onClick={() => setStep(step + 1)} className="rounded-full bg-ville-indigo px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-1627" data-mp="28h32ivxvtkbb">
                  {step === 3 ? `Pay ${activity.price}` : 'Continue'}
                </button>
              </div>}

            {step === 4 && <button type="button" onClick={close} className="mt-8 w-full rounded-full border border-slate-200 px-6 py-3 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo" data-id="element-1628" data-mp="2rd2uw7urupvf">
                Done
              </button>}
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}